import { ArrowDown, ArrowRight } from 'react-feather';
import { backgroundStyles, borderStyles, fontStyles } from 'styles/theme';
import AndContainer from 'components/common/AndContainer';
import { Arrangement } from 'types/arrangement';
import ArrangementDetailsCard from '../ArrangementDetailsCard';
// import { ArrowContainer } from 'components/common/ArrowIcon/components';

import { Articulation } from 'types/articulation';
import { Card as CommonCard } from 'components/common/Card';
import { Course } from 'types/course';
import { CourseCohort } from '@uac-au/advance-typings';
import { CreditType } from 'types/credit';
import FlexContainer from 'components/common/FlexContainer';
import { Fragment } from 'react';
import ImageWithFilter from 'components/common/ImageWithFilter';
import { RequiredCredential } from 'types/required-credential';
import RequiredCredentialsSection from 'components/credits/credit-details/RequiredCredentialsSection';
import { Spin } from 'antd';
import { Strong } from 'components/common/Strong';
import SubjectsSection from 'components/credits/credit-details/SubjectsSection';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import { mobileMediaQuery } from 'utils/common';
import styled from '@emotion/styled';
import useFormSchema from 'hooks/use-form-schema';
import useIsMobile from 'hooks/use-is-mobile';
import useTranslation from 'hooks/use-translation';
import useURLs from 'hooks/use-url';

export const Heading = styled.span({
  fontSize: 18,
  lineHeight: '20px',
  fontWeight: 'bold',
  [mobileMediaQuery]: {
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: 'bold',
  },
});

export const MiniLogo = styled.img({
  height: 56,
  float: 'right',
});

export const SubHeading = styled.span({
  lineHeight: '20px',
  fontSize: '14px',
  color: fontStyles.colors.tertiary,
  [mobileMediaQuery]: {
    lineHeight: '24px',
    fontSize: '12px',
  },
});

export const Card = styled(CommonCard)({
  background: 'white',
  [mobileMediaQuery]: {
    borderRadius: 0,
  },
  borderStyle: 'none',
});

export const TopSection = styled(FlexContainer)({
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  padding: 48,
  // borderBottom: `1px solid ${borderStyles.colors.primary}`,
  gap: 24,
  // '&:last-of-type': {
  //   borderBottom: 'initial',
  // },
  [mobileMediaQuery]: {
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'center',
    padding: '24px 20px',
    gap: 24,
  },
});

type SourceCredentialsSectionProps = {
  requiredCredentials: RequiredCredential[];
};

export function SourceCredentialsSection(props: SourceCredentialsSectionProps): JSX.Element {
  const { requiredCredentials } = props;
  const formSchema = useFormSchema();
  const translation = useTranslation();

  if (!formSchema || !translation) {
    return <Spin />;
  }
  return (
    <SourceCredentialsContainer>
      <Strong>{translation.details.sourceDescription}</Strong>

      {requiredCredentials.map((credential, index, array) => {
        const { heading, subHeading, unjoinedHeadings, unjoinedSubHeadings } = getHeadingAndSubHeading(
          credential,
          formSchema,
        );
        return (
          <Fragment key={`${heading}${subHeading}`}>
            <Heading className='source-credential-heading'>
              {unjoinedHeadings.map((heading) => (
                <span key={heading.propertyName} className={heading.propertyName}>
                  {heading.value + ' '}
                </span>
              ))}
            </Heading>
            <SubHeading className='source-credential-subheading'>
              {unjoinedSubHeadings.map((subheading) => (
                <span key={subheading.propertyName} className={subheading.propertyName}>
                  {subheading.value + ' '}
                </span>
              ))}
            </SubHeading>
            {index !== array.length - 1 && <AndContainer />}
          </Fragment>
        );
      })}
    </SourceCredentialsContainer>
  );
}

const SourceCredentialsContainer = styled(FlexContainer)({
  flex: 1,
  gap: 8,
  border: `1px solid ${borderStyles.colors.primary}`,
  borderRadius: 6,
  padding: '28px',
  alignSelf: 'stretch',
  textAlign: 'left',
});

export const ArrowContainer = styled(FlexContainer)({
  width: 58,
  height: 58,
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  border: `1px solid ${backgroundStyles.colors.tertiary}`,
  [mobileMediaQuery]: {
    width: 32,
    height: 32,
  },
});

export function ArrowSection(): JSX.Element {
  const isMobile = useIsMobile();
  return (
    <ArrowContainer className='articulation-details-arrow-container'>
      {isMobile ? <ArrowDown size={16} /> : <ArrowRight size={24} />}
    </ArrowContainer>
  );
}

export type TargetCourseSectionProps = {
  articulation: Articulation;
};

export function TargetCourseSection(props: TargetCourseSectionProps): JSX.Element {
  const { articulation } = props;
  const translation = useTranslation();
  const urls = useURLs();

  if (!translation) {
    return <Spin />;
  }

  return (
    <TargetCourseContainer>
      <Strong>{translation.details.targetDescription}</Strong>
      <TargetCourseContent course={articulation.course} />
      <FlexContainer gap={4}>
        <SubHeading className='course-code-label'>Course code {articulation.course.code}</SubHeading>
        <CricosCodeContent course={articulation.course} />
        <SubHeading className='institution-label'>{translation.institution}</SubHeading>
      </FlexContainer>
      <CricosViewCourseSection gap={4}>
        <CohortContent articulation={articulation} />
        <ViewCourseContent course={articulation.course} />
      </CricosViewCourseSection>
      <div>
        <MiniLogo alt={urls?.logo.alt} src={`config/icons/${translation.common.icons.logo.src}`} />
      </div>
    </TargetCourseContainer>
  );
}

const CricosViewCourseSection = styled(FlexContainer)({
  gap: 4,
  [mobileMediaQuery]: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
  },
});

const TargetCourseContainer = styled(FlexContainer)({
  flex: 1,
  gap: 8,
  borderRadius: 6,
  padding: '28px',
  alignSelf: 'stretch',
  backgroundColor: backgroundStyles.colors.quaternary,
  textAlign: 'left',
});

type TargetCourseContentProps = {
  course: Course;
};

export function TargetCourseContent(props: TargetCourseContentProps): JSX.Element {
  const {
    course: { name, url },
  } = props;

  const courseName = <CourseName className='target-course-label'>{name}</CourseName>;

  if (!url) {
    return courseName;
  }
  return (
    <Anchor href={url} target='_blank' rel='noopener noreferrer'>
      {courseName}
    </Anchor>
  );
}

const Anchor = styled.a({
  display: 'flex',
});

const CourseName = styled.span({
  color: fontStyles.colors.primary,
  fontSize: 18,
  lineHeight: '20px',
  fontWeight: 'bold',
  [mobileMediaQuery]: {
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: 'bold',
  },
});

type CricosCodeContentProps = {
  course: Course;
};

export function CricosCodeContent(props: CricosCodeContentProps): JSX.Element | null {
  const {
    course: { cricosCodes },
  } = props;

  if (cricosCodes.length === 0) {
    return null;
  }

  const cricosString = `CRICOS ${cricosCodes.join(', ')}`;
  return <SubHeading className='cricos-label'>{cricosString}</SubHeading>;
}

type CohortContentProps = {
  articulation: Articulation;
};

export function CohortContent(props: CohortContentProps): JSX.Element | null {
  const { articulation } = props;

  const translation = useTranslation();

  if (!translation) {
    return null;
  }

  return (
    <Fragment>
      {articulation.course.cohorts.map((cohort) => {
        const cohortKey = cohort === CourseCohort.INTERNATIONAL ? 'international' : 'domestic';
        const { filter, label } = translation.common.cohorts[cohortKey];
        return (
          <FlexContainer flexDirection='row' gap={4} key={cohort}>
            <ImageWithFilter
              src={`./config/icons/${translation.common.icons[cohortKey].src}`}
              alt=''
              width={16}
              filter={filter}
            />
            <SubHeading className='cohort-label'>{label}</SubHeading>
          </FlexContainer>
        );
      })}
    </Fragment>
  );
}

type AdmissionCriteriaContentProps = {
  articulation: Articulation;
};

export function AdmissionCriteriaContent(props: AdmissionCriteriaContentProps): JSX.Element | null {
  const { articulation } = props;
  const translation = useTranslation();
  if (!translation) {
    return null;
  }

  const additionalAdmissionCriteria = articulation.arrangements.some(
    (arrangement) => arrangement.additionalAdmissionCriteria,
  );

  if (!additionalAdmissionCriteria) {
    return null;
  }

  return (
    <FlexContainer gap={4} flexDirection='row' alignItems='center'>
      <img
        src={`./config/icons/${translation.common.icons.infoFilled.src}`}
        alt={translation.common.icons.infoFilled.alt}
        width={16}
        height={16}
      />
      <SubHeading className='admission-criteria-label'>Admission criteria applies</SubHeading>
    </FlexContainer>
  );
}

type ViewCourseContentProps = {
  course: Course;
};

export function ViewCourseContent(props: ViewCourseContentProps): JSX.Element | null {
  const { course } = props;
  const translation = useTranslation();
  if (!translation) {
    return null;
  }

  if (!course.url) {
    return null;
  }

  return (
    <a
      href={course.url}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={`View course ${course.name} in a new tab`}
    >
      <ViewCourseContainer gap={4} flexDirection='row' alignItems='center'>
        <img src={`./config/icons/${translation.common.icons.externalLink.src}`} alt='' width={16} height={16} />
        <span className='view-course-link'>{translation.home.searchResultTab.viewArticulationsTab.viewButton}</span>
      </ViewCourseContainer>
    </a>
  );
}

const ViewCourseContainer = styled(FlexContainer)({
  fontSize: '14px',
  [mobileMediaQuery]: {
    fontSize: '12px',
  },
});

export const BottomSection = styled(FlexContainer)({
  margin: 24,
  gap: 16,
  [mobileMediaQuery]: {
    margin: '24px 22px',
  },
});

export function BottomSectionHeading(): JSX.Element {
  return <BottomSectionHeadingContainer>Subject credit breakdown</BottomSectionHeadingContainer>;
}

const BottomSectionHeadingContainer = styled.div({
  color: fontStyles.colors.primary,
  fontSize: 20,
  fontWeight: 'bold',
  lineHeight: '32px',
  padding: '0px 24px',
  [mobileMediaQuery]: {
    padding: 0,
  },
});

type ArrangementCardsProps = {
  arrangements: Arrangement[];
};

export function ArrangementCards(props: ArrangementCardsProps): JSX.Element {
  const { arrangements } = props;

  return (
    <Fragment>
      <ArrangementDetailsCard arrangements={arrangements} />
    </Fragment>
  );
}

type ArrangementCardProps = {
  arrangement: Arrangement;
};

export function ArrangementCard(props: ArrangementCardProps): JSX.Element | null {
  const { arrangement } = props;

  return (
    <CommonCard className='arrangement-details-card'>
      <RequiredCredentialsSection requiredCredentials={arrangement.requiredCredentials} />
      <SubjectsSection creditType={CreditType.ARTICULATION} arrangement={arrangement} />
    </CommonCard>
  );
}
